import {type WCCartItem} from '../../../@type/woocommerce/cart-item';
import {bundleQuantityLabel, cartItemDisplayAmount, cartItemLabel, cartItemQuantity, cartItemVariationHTML} from '../util/cart';
import {store} from '../store';
import {MerchantConfiguration} from '../reducers/merchantConfigurationReducer';
import {DefaultCart} from '../reducers/cartReducer';
import {Feature} from '../reducers/environmentReducer';
import {initQuantityChangerEvent} from './quantityChanger';
import {getLocaleText} from '../util/translation';
import {$qs, $qsAll} from '../../../@shared/ts/dom';
import {FeatureFlag} from '../../../@type/features';
import trashcanURL from '../../img/trashcan.svg';
import trashcanRedURL from '../../img/trashcan-red.svg';
import qtyMinusImageURL from '../../img/qty-minus.svg';
import qtyPlusImageURL from '../../img/qty-plus.svg';

export function initCart() {
	initCartEvents();
	initQuantityChangerEvent();
}

function initCartEvents() {
	/**
	 * Handle cart rerendering
	 * Uses a captured variable for culling unneeded rendering
	 */
	let previousCartData = '';
	let previousCurrencyData = '';
	store.subscribe(() => {
		// To avoid noticeable cart rerendering from images reloading after any state changes we check if the current cart has
		// changed by stringifying the cart data and comparing strings.
		// For plain objects it is the fastest method and incredibly simple: https://stackoverflow.com/questions/1068834/object-comparison-in-javascript
		// This also now extends to price totals if the total is not the same we will re render the cart
		const cartData = JSON.stringify(DefaultCart.contents());
		const currencyData = JSON.stringify(MerchantConfiguration.currency.configuration());
		if (cartData !== previousCartData || currencyData !== previousCurrencyData) {
			previousCartData = cartData;
			previousCurrencyData = currencyData;
			renderOrderSummaryItems(DefaultCart.contents());
		}
	});
}

function renderOrderSummaryItems(cart: WCCartItem[]): void {
	const $tbody = $qs('#pp-summary-body');
	const $tbodyMobile = $qs('#pp-summary-body-mobile');

	if (!$tbody || !$tbodyMobile) {
		return;
	}

	clearOrderSummary();

	if (DefaultCart.contents().length === 0) {
		const $message = `<div class="pp-order-summary-item"><p>${getLocaleText('Cart is empty')}</p></div>`;

		$tbody.innerHTML = $message;
		$tbodyMobile.innerHTML = $message;
		return;
	}

	for (let i = 0; i < cart.length; i++) {
		const item = cart[i];
		// If an item has no quantity, there is no need to show it.
		if (!item || cartItemQuantity(item) === 0) {
			continue;
		}

		let $cartRow;
		if (item.is_part_of_bundle) {
			continue;
		} else {
			const itemBundle: WCCartItem[] = [];
			itemBundle.push(item);
			// In the case of bundles, render the item's bundle items with it
			for (let j = i + 1; j < cart.length; j++) {
				const nextItem = cart[j];
				if (nextItem?.is_part_of_bundle) {
					itemBundle.push(nextItem);
				} else {
					break;
				}
			}

			$cartRow = renderCartItem(itemBundle);
		}

		if (!$cartRow) {
			return;
		}

		// Add a separator border if wanted here
		if (i !== 0) {
			$cartRow.classList.add('pp-order-summary-item-border');
		}

		$tbody.append($cartRow);
		$tbodyMobile.append($cartRow.cloneNode(true));
	}
}

function renderBundleItem(item: WCCartItem) {
	const $imgDiv = getImgQtyDiv(item);

	const $labelDiv = document.createElement('div');
	$labelDiv.className = 'pp-bundle-label';
	$labelDiv.innerHTML = `${cartItemLabel(item)}${bundleQuantityLabel(item)}`;

	const $bundleItemTopRow = document.createElement('div');
	$bundleItemTopRow.className = 'pp-cart-item-info';
	$bundleItemTopRow.appendChild($labelDiv);

	const $amountDiv = document.createElement('div');
	$amountDiv.className = 'pp-item-amount';
	const $amountP = document.createElement('p');
	$amountP.classList.add('pp-recalculate-blur'); // Connected to price-update-on-blur behavior
	const $amount = cartItemDisplayAmount(item);
	if (parseFloat($amount.replace(/^.*;/, '')) > 0) {
		$amountP.innerHTML = $amount;
	} else {
		$amountP.innerHTML = '--';
	}

	$amountDiv.appendChild($amountP);
	$bundleItemTopRow.appendChild($amountDiv);

	const $bundleItemSecondRow = document.createElement('div');
	$bundleItemSecondRow.className = 'pp-cart-item-info';
	$bundleItemSecondRow.innerHTML = cartItemVariationHTML(item);

	const $bundleItemInfoDiv = document.createElement('div');
	$bundleItemInfoDiv.className = 'pp-cart-item-info-container';
	$bundleItemInfoDiv.appendChild($bundleItemTopRow);
	$bundleItemInfoDiv.appendChild($bundleItemSecondRow);

	const $bundleItem = document.createElement('div');
	$bundleItem.className = 'pp-bundle-summary-item';
	$bundleItem.appendChild($imgDiv);
	$bundleItem.appendChild($bundleItemInfoDiv);
	return $bundleItem;
}

function renderCartItem(itemBundle: WCCartItem[]): HTMLDivElement | null {
	const item = itemBundle[0];
	const $cartRow = document.createElement('div');

	if (!item) {
		return null;
	}

	const imageSrc = item.image?.[0] ?? '';
	const showImage = Feature.enabled(FeatureFlag.PRODUCT_IMAGES) && imageSrc && imageSrc !== 'unknown' && imageSrc !== '(unknown)';
	$cartRow.className = 'pp-order-summary-item';

	if (!showImage) {
		$cartRow.style.gap = '16px';
	}

	// $imgQtyDiv and $itemInfoContainer are the 2 members of the outer flex container ($cartRow)
	const $imgQtyDiv = getImgQtyDiv(item);
	// $itemInfoContainer is a vertical flex box that contains $itemInfo, $variationInfo, and each $bundleItem as its flex items
	const $itemInfoContainer = document.createElement('div');
	$itemInfoContainer.className = 'pp-cart-item-info-container';
	// $itemInfo is a horizontal flexbox that contains all the current item's info
	const $itemInfo = document.createElement('div');
	$itemInfo.className = 'pp-cart-item-info';

	const $labelDiv = document.createElement('div');
	$labelDiv.className = 'pp-item-label';
	$labelDiv.innerHTML = cartItemLabel(item);
	$itemInfo.appendChild($labelDiv);

	const $amountDiv = document.createElement('div');
	$amountDiv.className = 'pp-item-amount';
	const $amountP = document.createElement('p');
	$amountP.classList.add('pp-recalculate-blur'); // Connected to price-update-on-blur behavior
	$amountP.innerHTML = cartItemDisplayAmount(item);
	$amountDiv.appendChild($amountP);
	$itemInfo.appendChild($amountDiv);

	const $removerDiv = document.createElement('div');
	$removerDiv.className = 'pp-item-remover';
	$removerDiv.innerHTML = `<button class="pp-item-remover-btn" data-qid="${item.item_key ?? ''}">
	<img src="${trashcanURL}" class="pp-item-remover-img"/>
	<img src="${trashcanRedURL}" class="pp-item-remover-hover-img"/>
	</button>`;
	$itemInfo.appendChild($removerDiv);

	$itemInfoContainer.appendChild($itemInfo);

	// Render variation info
	const $variationInfo = document.createElement('div');
	$variationInfo.className = 'pp-cart-item-info';
	$variationInfo.innerHTML = cartItemVariationHTML(item);
	$itemInfoContainer.appendChild($variationInfo);

	// Render bundle items
	for (let i = 1; i < itemBundle.length; i++) {
		const bundleItem = itemBundle[i];
		if (!bundleItem) {
			continue;
		}

		const $bundleItem = renderBundleItem(bundleItem);
		$itemInfoContainer.appendChild($bundleItem);
	}

	if ($imgQtyDiv.innerHTML !== '') {
		$cartRow.appendChild($imgQtyDiv);
	}

	$cartRow.appendChild($itemInfoContainer);
	return $cartRow;
}

function clearOrderSummary(): void {
	for (const $item of $qsAll('.pp-order-summary-item')) {
		$item.remove();
	}
}

function getImgQtyDiv(item: WCCartItem) {
	const imageSrc = item.image?.[0] ?? '';
	const showImage = Feature.enabled(FeatureFlag.PRODUCT_IMAGES) && imageSrc && imageSrc !== 'unknown' && imageSrc !== '(unknown)';
	const showQuantityChanger = Feature.enabled(FeatureFlag.QUANTITY_CHANGER) && !item.is_part_of_bundle;

	const $div = document.createElement('div');
	$div.className = 'pp-cart-img-qty';

	if (!showImage && !showQuantityChanger) {
		if (item.is_part_of_bundle) {
			$div.className = 'pp-cart-img';
			return $div;
		}

		$div.className = 'pp-cart-img-qty-empty';
		$div.innerHTML += buildQuantityChanger(showImage, item);
		return $div;
	}

	if (showImage) {
		if (item.is_part_of_bundle) {
			$div.className = 'pp-cart-img';
			$div.innerHTML = `<div class="product-img-td-b0"><img class="pp-bundle-img-size" src="${item.image?.[0] ?? ''}"/></div>`;
			return $div;
		}

		$div.innerHTML = `<div class="product-img-td-b0"><img class="pp-product-img-size" src="${item.image?.[0] ?? ''}"/></div>`;

		$div.innerHTML += buildQuantityChanger(showImage, item);
	} else {
		const $qtyContainer = document.createElement('div');
		$qtyContainer.className = 'pp-cart-img-qty-empty';
		$qtyContainer.innerHTML = buildQuantityChanger(showImage, item);
		$div.append($qtyContainer);
	}

	return $div;
}

function buildQuantityChanger(showImage: boolean | string, item: WCCartItem) {
	const width = `${cartItemQuantity(item)}`.length + 1;
	const showQuantityChanger = Feature.enabled(FeatureFlag.QUANTITY_CHANGER) && !item.is_part_of_bundle;
	const quantityNumber = `<div class="pp-qty-changer-disabled ${showImage ? 'pp-qty-changer-with-img' : ''}">${cartItemQuantity(item)}</div>`;
	const quantityChanger = `
	<div class="quantity-changer ${showImage ? 'pp-qty-changer-with-img' : ''}">
		<button type="button" class="flex-center qty-btn decrease-qty ${cartItemQuantity(item) <= 1 ? 'pp-qty-scroll-end' : 'pp-qty-btn-hide'}" data-qid="${item.item_key ?? ''}">
			<img class="pp-qty-symbol" src="${qtyMinusImageURL}">
		</button>
		<input type="number" min="0" max="${item.stock_qty ? item.stock_qty : ''}" class="qty-fs" value="${cartItemQuantity(item)}" data-qid="${item.item_key ?? ''}" required style="width: ${width}ch;" role="presentation"/>
		<button type="button" class="flex-center qty-btn increase-qty ${(item.stock_qty && cartItemQuantity(item) >= item.stock_qty) ? 'pp-qty-scroll-end' : 'pp-qty-btn-hide'}" data-qid="${item
	.item_key ?? ''}">
			<img class="pp-qty-symbol" src="${qtyPlusImageURL}">
		</button>
	</div>`;
	return showQuantityChanger ? quantityChanger : quantityNumber;
}
